import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set auth header synchronously so child effects on first mount (which run before
// AuthProvider's own useEffect) already send the token after a hard reload.
const _initialToken = localStorage.getItem('token');
if (_initialToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_initialToken}`;
}

// Store user data in localStorage for persistence
const saveUserToStorage = (userData) => {
  if (userData) {
    localStorage.setItem('user', JSON.stringify(userData));
  } else {
    localStorage.removeItem('user');
  }
};

const getUserFromStorage = () => {
  try {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  // Initialize from localStorage for instant load
  const [user, setUser] = useState(getUserFromStorage());
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const authCheckInProgress = useRef(false);
  const lastAuthCheck = useRef(0);

  // Set axios header immediately if we have a token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Only clear auth on explicit auth endpoint 401s, not all 401s
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only clear auth if it's specifically an auth endpoint that failed
        const isAuthEndpoint = error.config?.url?.includes('/auth/me') || 
                               error.config?.url?.includes('/auth/login');
        
        if (error.response?.status === 401 && isAuthEndpoint) {
          console.log('Auth endpoint returned 401, clearing session');
          clearAuth();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchUser = useCallback(async (force = false) => {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress.current && !force) {
      return;
    }

    // Don't check more than once every 30 seconds unless forced
    const now = Date.now();
    if (!force && (now - lastAuthCheck.current) < 30000) {
      setLoading(false);
      return;
    }

    authCheckInProgress.current = true;
    lastAuthCheck.current = now;

    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      saveUserToStorage(res.data);
    } catch (err) {
      console.log('Auth check failed:', err.response?.status, err.message);
      
      // Only clear if it's definitely a 401 unauthorized
      if (err.response?.status === 401) {
        clearAuth();
      }
      // For network errors, keep the cached user data
      // They might just be temporarily offline
    } finally {
      setLoading(false);
      authCheckInProgress.current = false;
    }
  }, [clearAuth]);

  // Initial auth check
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  // Periodic auth refresh (every 5 minutes while tab is active)
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchUser();
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Also refresh when tab becomes visible after being hidden
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && token) {
        fetchUser();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [token, fetchUser]);

  const login = useCallback((userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    saveUserToStorage(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    lastAuthCheck.current = Date.now();
  }, []);

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    saveUserToStorage(userData);
  }, []);

  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    isAdmin,
    login,
    logout,
    updateUser,
    fetchUser: () => fetchUser(true) // Force refresh when called manually
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
